// @flow

import * as React from 'react';

import type { RequestPromise } from '@insolus/request';

export type Requester<DataType> = (data: any) => RequestPromise<DataType>;

type Executer<DataType> = (data: any) => RequestPromise<DataType>;

type ReturnType<DataType> = {|
    execute: Executer<DataType>,
    isExecuting: boolean
|};

const useRequest = function <DataType> (
    requester: Requester<DataType>
): ReturnType<DataType> {
    const [isExecuting, setIsExecuting] = React.useState<boolean>(false);

    const _request = React.useRef<RequestPromise<DataType> | void>(undefined);

    const execute = (data: any): RequestPromise<DataType> => {
        if (isExecuting) {
            const promise: any = Promise.resolve();
            promise.abort = () => {};
            return promise;
        }

        setIsExecuting(true);

        _request.current = requester(data);
        const _promise: any = _request.current.finally(() => {
            setIsExecuting(false);
        });
        _promise.abort = () => {
            if (_request.current) {
                _request.current.abort();
            }
        };
        return _promise;
    };

    React.useEffect(
        () => () => {
            if (_request.current) {
                _request.current.abort();
            }
        },
        []
    );

    return { execute, isExecuting };
};

export default useRequest;
